import type { MarketRate } from "@cede/types";
import { getCurrentRate } from "@cede/utils";
import { parseDecimalInput, safeDecimal } from "./numeric";
import type { OrderSide, OrderType } from "@/components/features/perps/types";
import { HYPERLIQUID_VALIDATION_MESSAGES, getHyperliquidTpslValidationMessage } from "@/constants/hyperliquid/trading";
import Decimal from "decimal.js";

const MIN_ORDER_VALUE_USD = new Decimal("10");

export interface ValidatePerpSubmitInput {
  size: string;
  quoteAmount: string;
  orderType: OrderType;
  limitPrice?: string;
  orderSide: OrderSide;
  showTP: boolean;
  takeProfitPrice: string;
  showSL: boolean;
  stopLossPrice: string;
  markPrice?: string;
  marketRate: MarketRate | null;
}

export type ValidatePerpSubmitResult = { ok: true; entryPrice: Decimal } | { ok: false; error: string };

export function validateHyperliquidPerpSubmit(input: ValidatePerpSubmitInput): ValidatePerpSubmitResult {
  const {
    size,
    quoteAmount,
    orderType,
    limitPrice,
    orderSide,
    showTP,
    takeProfitPrice,
    showSL,
    stopLossPrice,
    markPrice,
    marketRate,
  } = input;

  const sizeValue = parseDecimalInput(size);
  if (!sizeValue || sizeValue.lte(0)) {
    return { ok: false, error: HYPERLIQUID_VALIDATION_MESSAGES.sizePositive };
  }

  const quoteValue = parseDecimalInput(quoteAmount);
  if (!quoteValue || quoteValue.lt(MIN_ORDER_VALUE_USD)) {
    return { ok: false, error: `Order value must be at least ${MIN_ORDER_VALUE_USD.toString()} USD` };
  }

  let entryPrice = safeDecimal(markPrice, "0");
  if (orderType === "market" && marketRate) {
    const executionPrice = getCurrentRate(marketRate, orderSide === "long" ? "buy" : "sell");
    entryPrice = safeDecimal(executionPrice, markPrice ?? "0");
  }

  if (orderType === "limit") {
    const priceValue = parseDecimalInput(limitPrice);
    if (!priceValue || priceValue.lte(0)) {
      return { ok: false, error: HYPERLIQUID_VALIDATION_MESSAGES.limitPricePositive };
    }
    entryPrice = priceValue;
  }

  if (showTP) {
    const tpValue = parseDecimalInput(takeProfitPrice);
    if (!tpValue || tpValue.lte(0)) {
      return { ok: false, error: HYPERLIQUID_VALIDATION_MESSAGES.takeProfitPositive };
    }
    if (orderSide === "long" && tpValue.lte(entryPrice)) {
      return {
        ok: false,
        error: getHyperliquidTpslValidationMessage({ field: "takeProfit", side: orderSide, orderType }),
      };
    }
    if (orderSide === "short" && tpValue.gte(entryPrice)) {
      return {
        ok: false,
        error: getHyperliquidTpslValidationMessage({ field: "takeProfit", side: orderSide, orderType }),
      };
    }
  }

  if (showSL) {
    const slValue = parseDecimalInput(stopLossPrice);
    if (!slValue || slValue.lte(0)) {
      return { ok: false, error: HYPERLIQUID_VALIDATION_MESSAGES.stopLossPositive };
    }
    if (orderSide === "long" && slValue.gte(entryPrice)) {
      return {
        ok: false,
        error: getHyperliquidTpslValidationMessage({ field: "stopLoss", side: orderSide, orderType }),
      };
    }
    if (orderSide === "short" && slValue.lte(entryPrice)) {
      return {
        ok: false,
        error: getHyperliquidTpslValidationMessage({ field: "stopLoss", side: orderSide, orderType }),
      };
    }
  }

  return { ok: true, entryPrice };
}
